import { postRenderRequest } from '@src/services/renderDispatch';
import {
  AspectRatio,
  VideoDimensionsHeight,
  VideoResolution,
} from '@src/types.ts/video';
import { getAudioDurationFromUrl } from '@src/utility/audio';
import { deepClone } from '@src/utility/deepClone';
import { pickUnderlyingVideoElements } from '@src/utility/stackedLayout';
import { v4 as uuid } from 'uuid';

import type { LogLevel } from '@src/monitoring';
import type { KaraokeConfig } from '@src/services/karaoke/karaokeTypes';
import type { AnalyticsStore, UserIdentityStore } from '@src/stores-v2';
import type {
  ExtraElementData,
  Showcase,
  Story,
  Video,
} from '@src/types.ts/story';

const WEBHOOK_URL = `${process.env.REACT_APP_API_URL!}/api/webhooks/render`;

type PublishingParams = {
  source: any;
  video: Video;
  story: Story;
  organization?: Showcase;
  resLevel: VideoResolution;
  renderUsingChunks: boolean;
  sourceVideoUrl: string;
  duration: number;
  songsData: any;
  // PLAT-10009: the PublishRecord this render writes back to, and its publish
  // command's group id. Optional — absent only for callers that don't create a
  // record (record-creation failure is fatal upstream, so the record-backed
  // paths always supply these); when absent the backend uses the legacy Video
  // write.
  publishRecordId?: string;
  groupId?: string;
  // When true, the render lambdas mirror legacy Video fields (video_status,
  // video_file_primary, etc.) in addition to writing the PublishRecord. Only the
  // canonical target (original language × record's AR × default resolution)
  // should set this; foreign-language renders must NOT touch the Video record.
  mirrorsToLegacy?: boolean;
};

type ConstructorParams = Pick<
  PublishingParams,
  'organization' | 'story' | 'video'
>;

class VideoPublisher {
  private analyticsStore: AnalyticsStore;

  private userIdentityStore: UserIdentityStore;

  private analyticsTxId: string | null = null;

  private publishingParams: PublishingParams | null = null;

  private constructorParams: ConstructorParams;

  private finalSource: any;

  constructor(
    publishingParams: ConstructorParams,
    analyticsStore: AnalyticsStore,
    userIdentityStore: UserIdentityStore,
  ) {
    this.constructorParams = publishingParams;
    this.analyticsStore = analyticsStore;
    this.userIdentityStore = userIdentityStore;
  }

  public async publish(
    publishingParams: Omit<PublishingParams, 'organization' | 'story'>,
  ): Promise<void> {
    this.publishingParams = {
      ...this.constructorParams,
      ...publishingParams,
    };

    // Ensure that the source is cloned to avoid mutation
    this.publishingParams.source = deepClone(this.publishingParams.source);

    // Remove hidden elements from source before publishing.
    // LayoutEngine hides parent elements (visible: false) when creating scene
    // representations. Without this filter, Creatomate could render audio from
    // both the hidden parent AND the visible representation, causing volume duplication.
    this.publishingParams.source = this.excludeHiddenElements(
      this.publishingParams.source,
    );

    const { source, video, story, resLevel, duration, songsData } =
      this.publishingParams;

    this.finalSource = this.adjustResolution(
      source,
      { width: story.originalVideo.width, height: story.originalVideo.height },
      resLevel,
      video.aspectRatio,
    );

    this.finalSource.duration = duration;
    this.finalSource = await this.fillAudioElementsDurationInSource(
      VideoPublisher.fillVideoCompositionsDurationInSource(source),
      songsData,
    );

    await this.sendRenderAPIRequest(
      this.createRenderPayload({
        source: this.finalSource,
      }),
    );
  }

  /**
   * Filters out hidden elements from the source before publishing.
   * LayoutEngine marks parent elements as hidden (visible: false) when creating
   * scene representations. Without filtering, Creatomate could render audio from
   * both the hidden parent AND the visible representation, causing volume duplication.
   */
  private excludeHiddenElements(
    source: Record<string, unknown>,
  ): Record<string, unknown> {
    if (!source?.elements || !Array.isArray(source.elements)) {
      return source;
    }
    return {
      ...source,
      elements: source.elements.filter(
        (element: Record<string, unknown>) => element.visible !== false,
      ),
    };
  }

  private createRenderPayload(overrideParams: { source: Record<string, any> }) {
    const {
      story,
      video,
      organization,
      sourceVideoUrl,
      publishRecordId,
      groupId,
      mirrorsToLegacy,
    } = this.publishingParams!;
    const { source } = overrideParams;
    const volumeKeyPointsData = this.transformVolumeKeyPointsData(
      video.extraElementData,
    );
    return {
      transactionId: this.analyticsTxId,
      analyticsContext: this.getAnalyticsContext(),
      source,
      extraElementData: volumeKeyPointsData,
      // PLAT-10009: the render lambda writes status/output back to this record
      // (and mirrors the legacy Video fields only when it is the canonical
      // target). Omitted when absent so the backend keeps its legacy behaviour.
      ...(publishRecordId && { publishRecordId }),
      ...(groupId && { groupId }),
      ...(mirrorsToLegacy != null && { mirrorsToLegacy }),
      videoId: video.id,
      videoTitle: video.title,
      organizationId: organization?.id,
      organizationName: organization?.title,
      ...(!story.useAws && {
        datoVideoUrl: story.originalVideo.url,
        datoVideoUploadId: story.originalVideo.id,
      }),
      storyId: story.id,
      storyName: story.title,
      editorName: this.userIdentityStore.currentIdentity?.name,
      webhook_url: WEBHOOK_URL,
      aspectRatio: video.aspectRatio,
      renderUsingChunks: this.shouldRenderUsingChunks(),
      sourceVideoUrl,
      sourceVideoDuration: story.originalVideo.video.duration,
      // trackedUserData: this.rootStore.analyticsStore.getTrackedUserData(),
    };
  }

  private shouldRenderUsingChunks(): boolean {
    const { story, renderUsingChunks, resLevel } = this.publishingParams!;
    return (
      renderUsingChunks &&
      (story.useAws ||
        resLevel === VideoResolution.Original ||
        resLevel === VideoResolution.HD)
    );
  }

  private transformVolumeKeyPointsData(
    extraElementData: Record<
      string,
      ExtraElementData | KaraokeConfig | null | undefined
    >,
  ) {
    return Object.entries(extraElementData || {}).reduce(
      (acc, [id, element]) => {
        if ((element as ExtraElementData)?.volumeKeyPoints?.length) {
          acc[id] = {
            id,
            volumeKeyPoints: (element as ExtraElementData).volumeKeyPoints,
          };
        }
        return acc;
      },
      {} as Record<string, { id: string; volumeKeyPoints?: any[] }>,
    );
  }

  private adjustResolution(
    source: Record<string, any>,
    originalVideoDimensions: { width: number; height: number },
    resLevel: VideoResolution,
    aspectRatio: AspectRatio,
  ): Record<string, any> {
    const { width, height } = this.getDimensionsForResLevel(
      originalVideoDimensions,
      resLevel,
    );
    source.width = width;
    source.height = height;

    if (this.shouldAdjustSourceAspectRatio(source, aspectRatio)) {
      source = this.setVideoDimensionByAspectRatio(source, aspectRatio);
    }

    return source;
  }

  private shouldAdjustSourceAspectRatio(
    source: Record<string, any>,
    aspectRatio: AspectRatio,
  ): boolean {
    const EPSILON = 0.000001;
    const sourceAspectRatio = source.width / source.height;

    let desiredAspectRatio: number;

    switch (aspectRatio) {
      case AspectRatio.AR_1_1:
        desiredAspectRatio = 1;
        break;
      case AspectRatio.AR_9_16:
        desiredAspectRatio = 9 / 16;
        break;
      default:
        desiredAspectRatio = 16 / 9;
    }

    return Math.abs(sourceAspectRatio - desiredAspectRatio) > EPSILON;
  }

  private setVideoDimensionByAspectRatio(
    source: Record<string, any>,
    aspectRatio: AspectRatio,
  ) {
    switch (aspectRatio) {
      case AspectRatio.AR_1_1:
        source.width = source.height;
        break;
      case AspectRatio.AR_9_16:
        source.width = source.height;
        source.height = (source.width * 16) / 9;
        break;
      case AspectRatio.AR_16_9:
        source.width = (source.height * 16) / 9;
    }
    return source;
  }

  private async fillAudioElementsDurationInSource(
    source: Record<string, any>,
    songsData: any,
  ) {
    await Promise.all(
      source.elements.map(async (element: any) => {
        if (element.type === 'audio' && !element.duration) {
          try {
            const song = songsData.find(
              (song: any) => song.url === element.source,
            );
            const trimStart = parseFloat(element.trim_start || '0');
            if (song) {
              element.duration =
                parseFloat(song.customData.duration) - trimStart;
            } else {
              element.duration =
                (await getAudioDurationFromUrl(element.source)) - trimStart;
            }
          } catch (err) {
            console.error('Error fetching audio duration:', err);
          }
        }
      }),
    );
    return source;
  }

  private static fillVideoCompositionsDurationInSource(
    source: Record<string, any>,
  ): Record<string, any> {
    for (const element of source.elements) {
      const videoElements = pickUnderlyingVideoElements(element);
      for (const videoElement of videoElements) {
        if (
          videoElement &&
          videoElement.id !== element.id &&
          videoElement.duration == null &&
          element.duration != null
        ) {
          videoElement.duration = element.duration;
        }
      }
    }
    return source;
  }

  private getDimensionsForResLevel(
    originalVideoDimensions: { width: number; height: number },
    resLevel: VideoResolution,
  ) {
    const originalVideoHeight =
      originalVideoDimensions.height || VideoDimensionsHeight.AR_16_9;
    const { width, height } = originalVideoDimensions;
    let newWidth;
    let newHeight;
    let scaleFactor;
    if (resLevel === VideoResolution.HD) {
      scaleFactor = Math.max(1, originalVideoHeight / 1080);
    } else if (resLevel === VideoResolution.High) {
      scaleFactor = Math.max(1, originalVideoHeight / 720);
    } else if (resLevel === VideoResolution.Low) {
      scaleFactor = Math.max(1, originalVideoHeight / 270);
    } else if (resLevel === VideoResolution.Medium) {
      scaleFactor = Math.max(1, originalVideoHeight / 480);
    } else {
      scaleFactor = 1;
    }
    newWidth = Math.ceil(width / scaleFactor);
    newHeight = Math.ceil(height / scaleFactor);
    return { width: newWidth, height: newHeight };
  }

  /**
   * Hand the payload to the render endpoint. Throws `RenderDispatchError` if no
   * render was started, so the caller can mark its PublishRecord failed.
   */
  private async sendRenderAPIRequest(renderPayload: any) {
    const { video, publishRecordId, groupId } = this.publishingParams!;
    await postRenderRequest(renderPayload, {
      videoId: video.id,
      publishRecordId,
      groupId,
    });
  }

  private getAnalyticsContext() {
    return {
      ...this.analyticsStore.getTrackedEnvData(),
      ...this.analyticsStore.getTrackedUserData(),
      showcase: {
        id: this.constructorParams.organization?.id,
        name: this.constructorParams.organization?.title,
      },
      story: {
        id: this.constructorParams.story.id,
        name: this.constructorParams.story.title,
      },
      video: {
        id: this.constructorParams?.video.id,
        name: this.constructorParams?.video.title,
        aspectRatio: this.publishingParams?.video.aspectRatio,
        resolutionLevel: this.publishingParams?.resLevel,
        duration: this.publishingParams?.duration,
      },
    };
  }

  public trackInit(event: any) {
    this.analyticsTxId = uuid();
    this.analyticsStore.trackEvent({
      logMethod: 'info',
      eventName: 'video_publish',
      label: 'video-publish-analytics',
      transactionId: this.analyticsTxId,
      extraProperties: {
        ...this.getAnalyticsContext(),
        ...event,
      },
    });
  }

  public analyticsLog(level: LogLevel, message: string, payload?: any) {
    this.analyticsStore.log({
      label: 'video-publish-analytics',
      level,
      transactionId: this.analyticsTxId,
      msg: message,
      extraProperties: {
        ...this.getAnalyticsContext(),
        ...payload,
      },
    });
  }
}

export default VideoPublisher;
